import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';

import naturalSortBy from '@bibliocommons/natural-sort-by';
import { bindAll } from '@bibliocommons/utils-react';
import { bibShape, libraryShape } from '@bibliocommons/bc-prop-types';
import RadioButton from 'app/components/shared/RadioButton';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import Checkbox from 'app/components/shared/Checkbox';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from 'app/components/shared/Table';
import ShortFormattedDate from 'app/components/shared/ShortFormattedDate';

function renderCellContent(item, field) {
  switch (field) {
    case 'availability.libraryStatus': {
      const status = item.getIn(['availability', 'libraryStatus']);
      return status && <span>{status}</span>;
    }

    case 'dueDate': {
      const dueDate = item.get('dueDate');
      return dueDate && <ShortFormattedDate value={dueDate} />;
    }

    default:
      return item.getIn(field.split('.'));
  }
}

export default class ItemLevelHoldsTable extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    this.state = {
      currentSortField: null,
      currentSortDirection: 'asc'
    };
  }

  getSupportedFields() {
    const { currentLibrary } = this.props;
    const supportedFields = ['callNumber', 'volume', 'copy'];
    const showVolumeSelectorCollection = currentLibrary.get('showVolumeSelectorCollection');
    const showVolumeSelectorStatus = currentLibrary.get('showVolumeSelectorStatus');
    const showVolumeSelectorDueDate = currentLibrary.get('showVolumeSelectorDueDate');

    if (showVolumeSelectorCollection) supportedFields.unshift('collection');
    if (showVolumeSelectorStatus) supportedFields.push('availability.libraryStatus');
    if (showVolumeSelectorDueDate) supportedFields.push('dueDate');

    supportedFields.unshift('itemId');
    return supportedFields;
  }

  getTableItems(supportedFields) {
    const { currentSortField, currentSortDirection } = this.state;

    const sortedItems = currentSortField
      ? this.props.holdableItems.sort(naturalSortBy(currentSortField, currentSortDirection))
      : this.props.holdableItems;

    return sortedItems.map(item =>
      Immutable.Map(supportedFields.map(field => [field, renderCellContent(item, field)]))
    );
  }

  handleSort(field) {
    if (this.state.currentSortField === field) {
      this.setState(state => ({
        currentSortDirection: state.currentSortDirection === 'asc' ? 'desc' : 'asc'
      }));
    } else {
      this.setState({
        currentSortField: field,
        currentSortDirection: 'asc'
      });
    }
  }

  renderSingleSelectionControl(item) {
    const isItemCurrentlyOnHold = this.props.holds.some(hold => hold.get('itemId') === item.get('itemId'));

    return (
      <RadioButton
        checked={this.props.selectedItems.includes(item.get('itemId'))}
        className="select-single"
        disabled={isItemCurrentlyOnHold}
        handleChange={() => this.props.handleSingleSelect(item)}
        id={`row-select-${item.get('itemId')}`}
        label={item.get('callNumber')}
        name="select-single"
        srOnlyLabel
        value={item.get('itemId')}
      />
    );
  }

  renderBatchSelectionControl(item) {
    const isItemCurrentlyOnHold = this.props.holds.some(hold => hold.get('itemId') === item.get('itemId'));

    return (
      <Checkbox
        checked={this.props.selectedItems.includes(item.get('itemId'))}
        dataKey="select-multiple"
        disabled={isItemCurrentlyOnHold}
        handleChange={() => this.props.handleBatchSelect(item)}
        id={`row-select-${item.get('itemId')}`}
        label={item.get('callNumber')}
        name="select-multiple"
        srOnlyLabel
        value={item.get('itemId')}
      />
    );
  }

  renderSelectionControl(item) {
    const policy = this.props.bib.getIn(['policy', 'materialPolicy'], Immutable.Map());
    return policy.get('supportsBatchVolumeHolds')
      ? this.renderBatchSelectionControl(item)
      : this.renderSingleSelectionControl(item);
  }

  renderCaption() {
    const { currentSortField, currentSortDirection } = this.state;
    return (
      currentSortField &&
      currentSortDirection && (
        <FormattedMessage
          id="table_sorted_by"
          values={{
            field: <FormattedMessage id={currentSortField} />,
            direction: <FormattedMessage id={currentSortDirection} />
          }}
        />
      )
    );
  }

  renderTableHeader(fields) {
    const { currentSortField, currentSortDirection } = this.state;
    const selectableHeading = (
      <TableHeading>
        <ScreenReaderMessage>
          <FormattedMessage id="table_headings_item_select" />
        </ScreenReaderMessage>
      </TableHeading>
    );

    return (
      <TableHeader>
        {selectableHeading}
        {fields.map(field => (
          <TableHeading
            sortable
            sortDirection={currentSortField === field ? currentSortDirection : undefined}
            handleSort={() => this.handleSort(field)}
            key={field}
          >
            <FormattedMessage id={field} />
          </TableHeading>
        ))}
      </TableHeader>
    );
  }

  renderRow(row, fields) {
    return (
      <TableRow
        className="bib-item-row"
        key={row.get('itemId')}
        selected={this.props.selectedItems.includes(row.get('itemId'))}
        renderSelectionControl={() => this.renderSelectionControl(row)}
      >
        {fields.map(field => (
          <TableCell key={field} label={<FormattedMessage id={field} />} blockLabel>
            {row.get(field)}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  render() {
    const supportedFields = this.getSupportedFields();
    const tableItems = this.getTableItems(supportedFields);

    // Remove columns with no data.
    const fields = supportedFields.filter(field => field !== 'itemId' && tableItems.some(row => !!row.get(field)));

    return (
      <div className="cp-item-level-holds-table">
        <Table caption={this.renderCaption()}>
          {this.renderTableHeader(fields)}
          <TableBody>{tableItems.map(row => this.renderRow(row, fields))}</TableBody>
        </Table>
      </div>
    );
  }
}

ItemLevelHoldsTable.propTypes = {
  bib: bibShape.isRequired,
  currentLibrary: libraryShape.isRequired,
  handleBatchSelect: PropTypes.func.isRequired,
  handleSingleSelect: PropTypes.func.isRequired,
  holdableItems: ImmutablePropTypes.list.isRequired,
  holds: ImmutablePropTypes.map.isRequired,
  selectedItems: PropTypes.object.isRequired
};
